/**
 * Simplified Offer Service
 * Handles offer processing and email workflow using the generalized infrastructure
 */

import logger from '../../../utils/logger';
import { updateOffersBatch } from '../SummaryCaseTable/services/realtimeOfferService';
import { updateCaseStatusGeneralized } from '../SummaryCaseTable/services/caseStatusUpdateService';
import { toMySQLDateTime } from '../../../common/helper_functions.js';
import { findNegotiationCaseByParcelId, getConversionFactor } from '../../../utils/conversionUtils';
import { isCaseSettled, isAssignmentComplete } from '../SummaryCaseTable/utils/helpers.js';
import { createOfferContext } from '../../../utils/offerContextUtils';
import { getAVUtil } from '../../../utils/getAVUtil';
import { applyOfferRoundingGuard } from '../Settlement/SettlementRounding.js';
import { getMostRecentOffer } from '../../../utils/mostRecentOfferUtils.js';
import { uploadOffersToS3 } from './s3UploadService.js';
import { getTownViewXLSKeys } from '../SummaryCaseTable/config/columns.js';
import {
  prepareOffersAndOptionalWorkupsEmail,
  generateWorkupsForCombinedFlow,
  offerSheetWorkupLinks,
} from '../EmailFactory/emailFactory.js';
import { validateOfferEmailWorkflow } from '../EmailFactory/Validations/offerEmailValidations.js';
import { usePersistedDataStore } from '../../store.js';
import { useOfferOptionsStore } from './offerOptionsStore.js';

/** Rebuild row list with S3 / NYSCEF file arrays from `negotiationObj.cases` (call after regen + store update). */
const mergeLatestS3FilesIntoCaseRows = (rows, negotiationObjWithCases) => {
  const cases = negotiationObjWithCases?.cases;
  if (!Array.isArray(rows) || !Array.isArray(cases)) return rows;
  return rows.map((row) => {
    const pid = row.parcel_id || row.id;
    const c = findNegotiationCaseByParcelId(negotiationObjWithCases, pid);
    if (!c) return row;
    const next = { ...row };
    if (Array.isArray(c.S3Files) && c.S3Files.length) next.S3Files = c.S3Files;
    if (Array.isArray(c.NyscefS3Files) && c.NyscefS3Files.length) next.NyscefS3Files = c.NyscefS3Files;
    return next;
  });
};

/** Most recent Aventine round Comments for batch payloads (undefined when absent). */
export const getMostRecentAventineComments = (caseItem) => {
  const result = getMostRecentOffer(caseItem?.Negotiations, 'Aventine');
  const comments = result?.offer?.Comments;
  return comments ?? undefined;
};

/** @returns {boolean} Whether the row has a non-empty, positive OfferValue (aligns with convertOfferValueForEmail). */
const hasOfferValue = (row) => {
  const v = row?.OfferValue;
  if (v === undefined || v === null || v === 'None' || v === '') return false;
  const n = Number(v);
  return !Number.isNaN(n) && n > 0;
};

/**
 * Convert OfferValue from AV to MV for email display
 * @param {Object} caseItem - Case data
 * @returns {number} Converted offer value in market value
 */
export const convertOfferValueForEmail = (caseItem) => {
  // For settled cases, use settlement value; otherwise use offer value
  if (!caseItem.isActive && caseItem.SettlementValue && caseItem.SettlementValue > 0) {
    // Convert AV settlement value to MV for email display
    const eq = caseItem.EQ;
    const rar = caseItem.RAR;
    const conversionFactor = getConversionFactor(eq, rar);
    if (conversionFactor > 0) {
      return Math.round(caseItem.SettlementValue / conversionFactor / 10) * 10;
    }
    return Math.round(caseItem.SettlementValue);
  }
  
  // Convert AV to MV for email display
  if (caseItem.OfferValue !== undefined && caseItem.OfferValue !== null && caseItem.OfferValue !== 'None' && caseItem.OfferValue !== '') {
    const offerValue = Number(caseItem.OfferValue);
    if (!isNaN(offerValue) && offerValue > 0) {
      // Convert AV to MV using LOA
      const eq = caseItem.EQ;
      const rar = caseItem.RAR;
      const conversionFactor = getConversionFactor(eq, rar);
      if (conversionFactor > 0) {
        return Math.round(offerValue / conversionFactor / 10) * 10;
      }
      return Math.round(offerValue);
    }
  }
  return 0;
};

/**
 * Convert offer data for processing
 * @param {Array} offers - Raw offer data from table
 * @returns {Array} Formatted offer data for processing
 */
const convertOffersForProcessing = (offers) => {
  logger.log('🔍 [OFFER SERVICE] Converting offers for processing:', {
    inputType: Array.isArray(offers) ? 'array' : typeof offers,
    inputLength: Array.isArray(offers) ? offers.length : 'N/A',
    sampleOffer: Array.isArray(offers) && offers.length > 0 ? offers[0] : null
  });
  
  if (!Array.isArray(offers)) {
    logger.warn('⚠️ [OFFER SERVICE] convertOffersForProcessing received non-array input:', offers);
    return [];
  }
  
  const convertedOffers = offers.map((offer, index) => {
    
    const {
      parcel_id,
      OfferValue,
      PortfolioOffer,
      ProprietyWorkup,
      ...otherFields
    } = offer;
    
    // Extract offer data from ProprietyWorkup if available
    const workup = ProprietyWorkup || {};
    const finalizedOffers = workup.finalizedOffers || [];
    
    const converted = {
      parcel_id,
      OfferValue: OfferValue || 0,
      PortfolioOffer: PortfolioOffer || null,
      finalizedOffers,
      workup,
      ...otherFields
    };
    
    // logger.log(`🔍 [OFFER SERVICE] Converted offer ${index + 1}:`, {
    //   parcel_id: converted.parcel_id,
    //   OfferValue: converted.OfferValue,
    //   PortfolioOffer: converted.PortfolioOffer,
    //   finalizedOffersCount: converted.finalizedOffers.length,
    //   workupKeys: Object.keys(converted.workup)
    // });
    
    return converted;
  });
  
  logger.log('✅ [OFFER SERVICE] Conversion complete:', {
    inputCount: offers.length,
    outputCount: convertedOffers.length,
    convertedOffers: convertedOffers.map(o => ({
      parcel_id: o.parcel_id,
      OfferValue: o.OfferValue,
      PortfolioOffer: o.PortfolioOffer
    }))
  });
  
  return convertedOffers;
};

/**
 * Process offers for email workflow
 * @param {Object|Array} caseObjectOrArray - Single case object or array of cases
 * @param {Object} context - Processing context
 * @param {string} context.muniCode - Municipality code
 * @param {number} context.taxYear - Tax year
 * @param {string} context.userId - User ID
 * @param {Object} context.user - User object
 * @param {number} context.liveOffer - LiveOffer value (0 = not sent, 1 = sent). Defaults to 0 for email workflow
 * @returns {Promise<Object>} Processing result
 */
export const processOffers = async (caseObjectOrArray, context) => {
  logger.log('🚀 [OFFER SERVICE] Starting processOffers:', {
    inputType: Array.isArray(caseObjectOrArray) ? 'array' : typeof caseObjectOrArray,
    inputLength: Array.isArray(caseObjectOrArray) ? caseObjectOrArray.length : 1,
    context: {
      muniCode: context.muniCode,
      taxYear: context.taxYear,
      userId: context.userId,
      hasUser: !!context.user
    }
  });
  
  try {
    // Normalize input to always be an array
    const caseObjects = Array.isArray(caseObjectOrArray) ? caseObjectOrArray : [caseObjectOrArray];
    
    logger.log('🔍 [OFFER SERVICE] Normalized input:', {
      originalType: Array.isArray(caseObjectOrArray) ? 'array' : typeof caseObjectOrArray,
      normalizedLength: caseObjects.length,
      sampleCase: caseObjects[0] ? {
        parcel_id: caseObjects[0].parcel_id,
        Address: caseObjects[0].Address,
        hasOfferValue: hasOfferValue(caseObjects[0]),
        OfferValue: caseObjects[0].OfferValue
      } : null
    });
    
    if (caseObjects.length === 0) {
      logger.error('❌ [OFFER SERVICE] No cases provided for processing');
      throw new Error('No cases provided for processing');
    }

    logger.log('🔍 [OFFER SERVICE] Filtering cases with an offer value...');
    const casesWithOffers = caseObjects.filter((caseObj) => hasOfferValue(caseObj));
    
    logger.log('🔍 [OFFER SERVICE] Filtering results:', {
      totalCases: caseObjects.length,
      casesWithOffers: casesWithOffers.length,
      casesWithoutOffers: caseObjects.length - casesWithOffers.length,
      casesWithOffersList: casesWithOffers.map(c => ({
        parcel_id: c.parcel_id,
        Address: c.Address,
        OfferValue: c.OfferValue
      }))
    });
    
    if (casesWithOffers.length === 0) {
      logger.error('❌ [OFFER SERVICE] No cases with an offer value found');
      throw new Error('No cases with an offer value found');
    }

    logger.log(`🔍 [OFFER SERVICE] Processing ${casesWithOffers.length} cases with an offer value`);

    // Convert offers for processing
    const processedOffers = convertOffersForProcessing(casesWithOffers);
    
    // Extract parcel IDs
    const parcelIds = processedOffers.map(offer => offer.parcel_id);
    logger.log('🔍 [OFFER SERVICE] Extracted parcel IDs:', parcelIds);
    
    // Prepare offers data for batch update
    const offersData = processedOffers.map(offer => ({
      parcel_id: offer.parcel_id,
      OfferValue: offer.OfferValue,
      PortfolioOffer: offer.PortfolioOffer,
      finalizedOffers: offer.finalizedOffers,
      workup: offer.workup
    }));
    
    logger.log('🔍 [OFFER SERVICE] Prepared offers data for batch update:', {
      count: offersData.length,
      offers: offersData.map(o => ({
        parcel_id: o.parcel_id,
        OfferValue: o.OfferValue,
        PortfolioOffer: o.PortfolioOffer,
        finalizedOffersCount: o.finalizedOffers.length
      }))
    });

    // Update offers in database
    let offersResult = { success: true };
    try {
      logger.log('🔄 [OFFER SERVICE] Starting database update...');
      
      // Convert offers data to the format expected by updateOffersBatch
      const batchOffers = offersData.map(offer => {
        const caseObject = caseObjects.find(c => c.parcel_id === offer.parcel_id);
        const currentAV = getAVUtil(caseObject) ?? 0;
        const offerValueAV = offer.OfferValue;
        
        // Apply unified offer rounding guard
        const finalOfferValue = applyOfferRoundingGuard(offerValueAV, currentAV);
        
        return {
          parcelId: offer.parcel_id,
          offerValue: finalOfferValue,
          portfolioOffer: offer.PortfolioOffer,
          offeredBy: 'Aventine',
          offerDate: new Date().toISOString(),
          comments: getMostRecentAventineComments(caseObject),
        };
      });

      logger.log('🔍 [OFFER SERVICE] Batch offers for database update:', {
        count: batchOffers.length,
        offers: batchOffers.map(o => ({
          parcelId: o.parcelId,
          offerValue: o.offerValue,
          portfolioOffer: o.portfolioOffer,
          offeredBy: o.offeredBy
        }))
      });

      logger.log('🔍 [OFFER SERVICE] Calling updateOffersBatch with context:', {
        muniCode: context.muniCode,
        taxYear: context.taxYear,
        caseType: 'Town',
        userId: context.userId,
        userAccess: { level: 'admin' }
      });

      // Use liveOffer from context if provided, otherwise default to 0 (not sent yet)
      // This allows the email workflow to keep offers as "not sent" until email is actually sent
      const liveOfferValue = context.liveOffer !== undefined ? context.liveOffer : 0;

      offersResult = await updateOffersBatch(
        batchOffers,
        {
          muniCode: context.muniCode,
          taxYear: context.taxYear,
          caseType: 'Town',
          userId: context.userId,
          userAccess: { level: 'admin' }
        },
        {
          liveOffer: liveOfferValue,
          updateFrontend: true
        }
      );
      
      logger.log('✅ [OFFER SERVICE] Offers updated successfully:', {
        success: offersResult.success,
        processedCount: offersResult.processedCount,
        error: offersResult.error
      });
    } catch (error) {
      logger.error('❌ [OFFER SERVICE] Failed to update offers:', {
        error: error.message,
        stack: error.stack,
        offersData: offersData.map(o => ({
          parcel_id: o.parcel_id,
          OfferValue: o.OfferValue
        }))
      });
      offersResult = { success: false, error: error.message };
    }

    // Update case status for all cases
    let caseStatusResult = { success: true };
    try {
      logger.log('🔄 [OFFER SERVICE] Starting case status update...');
      const currentDate = toMySQLDateTime(new Date());
      
      logger.log('🔍 [OFFER SERVICE] Case status update parameters:', {
        muniCode: context.muniCode,
        taxYear: context.taxYear,
        reviewType: 'Negotiation',
        parcelIds: parcelIds,
        fieldsToUpdate: { 
          assignee: context.userId, 
          DateCompleted: currentDate 
        },
        updateFrontend: true,
        forceUpdateCompleted: true
      });
      
      await updateCaseStatusGeneralized({
        muniCode: context.muniCode,
        taxYear: context.taxYear,
        reviewType: 'Negotiation',
        parcelIds: parcelIds,
        fieldsToUpdate: { 
          assignee: context.userId, 
          DateCompleted: currentDate 
        },
        updateFrontend: true,
        forceUpdateCompleted: true,
        villageRun: context.villageRun || 0,
      });
      
      logger.log('✅ [OFFER SERVICE] Case status updated successfully');
    } catch (error) {
      logger.error('❌ [OFFER SERVICE] Failed to update case status:', {
        error: error.message,
        stack: error.stack,
        parcelIds: parcelIds,
        context: {
          muniCode: context.muniCode,
          taxYear: context.taxYear,
          userId: context.userId
        }
      });
      caseStatusResult = { success: false, error: error.message };
    }
    
    // Return result - for single case, return single case format; for batch, return batch format
    const finalSuccess = caseStatusResult.success && offersResult.success;
    
    logger.log('🔍 [OFFER SERVICE] Final processing result:', {
      finalSuccess,
      caseStatusSuccess: caseStatusResult.success,
      offersSuccess: offersResult.success,
      caseStatusError: caseStatusResult.error,
      offersError: offersResult.error,
      parcelIds: parcelIds,
      isSingleCase: caseObjects.length === 1
    });
    
    if (caseObjects.length === 1) {
      const result = {
        success: finalSuccess,
        message: `Offers processed for case ${parcelIds[0]}`,
        caseId: parcelIds[0],
        timestamp: new Date().toISOString(),
        data: { 
          caseStatus: caseStatusResult, 
          offers: offersResult
        }
      };
      logger.log('✅ [OFFER SERVICE] Single case result:', result);
      return result;
    } else {
      const result = {
        success: finalSuccess,
        message: `Batch offers processed for ${parcelIds.length} cases`,
        caseIds: parcelIds,
        processedCount: parcelIds.length,
        timestamp: new Date().toISOString(),
        data: { 
          caseStatus: caseStatusResult, 
          offers: offersResult
        }
      };
      logger.log('✅ [OFFER SERVICE] Batch result:', result);
      return result;
    }
    
  } catch (error) {
    logger.error('❌ [OFFER SERVICE] Error processing offers:', {
      error: error.message,
      stack: error.stack,
      inputType: Array.isArray(caseObjectOrArray) ? 'array' : typeof caseObjectOrArray,
      inputLength: Array.isArray(caseObjectOrArray) ? caseObjectOrArray.length : 1,
      context: {
        muniCode: context.muniCode,
        taxYear: context.taxYear,
        userId: context.userId
      }
    });
    throw error;
  }
};

/**
 * Mark offers as sent - updates LiveOffer to 1 and case status
 * This should be called only after the email is successfully sent
 * @param {Array} parcelIds - Array of parcel IDs to mark as sent
 * @param {Object} negotiationObj - Negotiation object
 * @param {Object} user - User object
 * @returns {Promise<Object>} Result of the update
 */
export const markOffersAsSent = async (parcelIds, negotiationObj, user) => {
  logger.log('🔄 [OFFER SERVICE] Marking offers as sent for', parcelIds.length, 'parcels');
  
  try {
    if (!parcelIds || parcelIds.length === 0) {
      return { success: true, message: 'No parcels to mark as sent' };
    }

    if (!negotiationObj || !negotiationObj.cases) {
      throw new Error('No negotiation data available');
    }

    if (!user || !user.userId) {
      throw new Error('User information is required');
    }

    // Get the latest offer data from the negotiation object
    const offersToMarkAsSent = parcelIds.map(parcelId => {
      // Find the case to get the current offer value
      const caseItem = negotiationObj.cases.find(c => c.parcel_id === parcelId);
      if (!caseItem) return null;
      
      const offerValueAV = caseItem?.OfferValue || null;
      if (offerValueAV === null) return null;
      
      const currentAV = getAVUtil(caseItem) ?? 0;
      const finalOfferValue = applyOfferRoundingGuard(offerValueAV, currentAV);
      
      return {
        parcelId: parcelId,
        offerValue: finalOfferValue,
        portfolioOffer: caseItem?.PortfolioOffer || null,
        offeredBy: 'Aventine',
        offerDate: new Date().toISOString(),
        comments: getMostRecentAventineComments(caseItem),
      };
    }).filter(offer => offer !== null && offer.offerValue !== null);

    if (offersToMarkAsSent.length === 0) {
      logger.warn('⚠️ [OFFER SERVICE] No valid offers found to mark as sent');
      return { success: true, message: 'No valid offers to mark as sent' };
    }

    // Create context for batch update
    const context = createOfferContext(user, negotiationObj, 'Manual');

    // Step 1: Update offers to set LiveOffer to 1 (mark as sent)
    let offerUpdateResult = { success: false };
    try {
      logger.log('🔄 [OFFER SERVICE] Updating offers to mark as sent...');
      offerUpdateResult = await updateOffersBatch(offersToMarkAsSent, context, {
        liveOffer: 1,
        updateFrontend: true
      });

      if (offerUpdateResult.success) {
        logger.log(`✅ [OFFER SERVICE] Successfully marked ${offerUpdateResult.processed_offers} offers as sent`);
      } else {
        logger.warn('⚠️ [OFFER SERVICE] Failed to mark offers as sent:', offerUpdateResult.error);
      }
    } catch (error) {
      logger.error('❌ [OFFER SERVICE] Error updating offers:', {
        error: error.message,
        stack: error.stack
      });
      offerUpdateResult = { success: false, error: error.message };
    }

    // Step 2: Update case status (Negotiation DateCompleted)
    let caseStatusResult = { success: true };
    try {
      logger.log('🔄 [OFFER SERVICE] Updating case status for sent offers...');
      const currentDate = toMySQLDateTime(new Date());

      await updateCaseStatusGeneralized({
        muniCode: negotiationObj.MuniCode,
        taxYear: negotiationObj.TaxYear,
        reviewType: 'Negotiation',
        parcelIds: parcelIds,
        fieldsToUpdate: {
          assignee: user.userId,
          DateCompleted: currentDate
        },
        updateFrontend: true,
        forceUpdateCompleted: true,
        villageRun: negotiationObj?.villageRun || 0,
      });

      logger.log('✅ [OFFER SERVICE] Case status updated successfully');
    } catch (error) {
      logger.error('❌ [OFFER SERVICE] Failed to update case status:', {
        error: error.message,
        stack: error.stack,
        parcelIds: parcelIds
      });
      caseStatusResult = { success: false, error: error.message };
    }

    const finalSuccess = offerUpdateResult.success && caseStatusResult.success;

    return {
      success: finalSuccess,
      message: finalSuccess 
        ? `Successfully marked ${parcelIds.length} offer(s) as sent`
        : 'Partially failed to mark offers as sent',
      offerUpdate: offerUpdateResult,
      caseStatusUpdate: caseStatusResult,
      processedCount: offerUpdateResult.processed_offers || 0
    };

  } catch (error) {
    logger.error('❌ [OFFER SERVICE] Error marking offers as sent:', {
      error: error.message,
      stack: error.stack,
      parcelIds: parcelIds
    });
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Handle offer email workflow
 * @param {Object} options - Workflow options
 * @param {Array} options.selectedRows - Selected table rows
 * @param {Array} options.allRows - All table rows
 * @param {Object} options.negotiationObj - Negotiation object
 * @param {Object} options.user - User information
 * @param {Function} options.onComplete - Completion callback
 * @param {string} options.downloadUrl - S3 download URL (optional)
 * @returns {Promise<void>}
 */
export const handleOfferEmailWorkflow = async (options) => {
  logger.log('🚀 [OFFER WORKFLOW] Starting handleOfferEmailWorkflow:', {
    selectedRowsCount: options.selectedRows?.length || 0,
    allRowsCount: options.allRows?.length || 0,
    hasNegotiationObj: !!options.negotiationObj,
    hasUser: !!options.user,
    hasDownloadUrl: !!options.downloadUrl,
    hasOnComplete: !!options.onComplete
  });

  const {
    selectedRows,
    allRows,
    negotiationObj,
    user,
    onComplete,
    downloadUrl
  } = options;

  try {
    logger.log('🔍 [OFFER WORKFLOW] Validating input parameters...');
    
    if (!negotiationObj || !negotiationObj.cases) {
      logger.error('❌ [OFFER WORKFLOW] No negotiation data available');
      throw new Error('No negotiation data available');
    }

    if (!selectedRows || selectedRows.length === 0) {
      logger.error('❌ [OFFER WORKFLOW] No cases selected to process');
      throw new Error('No cases selected to process');
    }

    if (!user) {
      logger.error('❌ [OFFER WORKFLOW] User information is required');
      throw new Error('User information is required');
    }

    // Check user permissions
    if (user?.userAccess?.level === 'Training') {
      logger.error('❌ [OFFER WORKFLOW] Training users cannot send email offers');
      throw new Error('Training users cannot send email offers. This action is restricted.');
    }


    logger.log('🔍 [OFFER WORKFLOW] Filtering cases with an offer value (unsettled only)...');
    const casesWithOffers = selectedRows.filter((row) => hasOfferValue(row) && !isCaseSettled(row));
    
    const settledCasesFiltered = selectedRows.filter(row => isCaseSettled(row)).length;
    
    logger.log('🔍 [OFFER WORKFLOW] Filtering results:', {
      totalSelected: selectedRows.length,
      settledCasesFiltered: settledCasesFiltered,
      casesWithOffers: casesWithOffers.length,
      casesWithoutOffers: selectedRows.length - casesWithOffers.length - settledCasesFiltered,
      casesWithOffersList: casesWithOffers.map(c => ({
        parcel_id: c.parcel_id,
        Address: c.Address,
        OfferValue: c.OfferValue,
        CaseReviewComplete: isAssignmentComplete(c, 'CaseReview')
      }))
    });
    
    if (casesWithOffers.length === 0) {
      logger.error('❌ [OFFER WORKFLOW] No unsettled cases with an offer value selected');
      throw new Error('No unsettled cases with an offer value selected. Please select cases that have an offer value.');
    }

    // Calculate case statistics based on ALL cases in the dataset (allRows)
    // This shows the overall settlement status of the entire case set
    const totalCases = allRows.length;
    const settledCases = allRows.filter(row => isCaseSettled(row)).length;
    const openCases = totalCases - settledCases;

    const caseInfo = {
      totalCases,
      openCases,
      settledCases
    };

    // Add comprehensive logging to debug the issue
    logger.log('🔍 [OFFER SERVICE] Case counting debug:', {
      allRowsTotal: allRows.length,
      casesWithOffersTotal: casesWithOffers.length,
      overallSettledCases: settledCases,
      overallOpenCases: openCases,
      selectedCasesSettled: casesWithOffers.filter(row => isCaseSettled(row)).length,
      selectedCasesOpen: casesWithOffers.filter(row => !isCaseSettled(row)).length,
      sampleCasesWithOffers: casesWithOffers.slice(0, 3).map(c => ({
        parcel_id: c.parcel_id,
        Address: c.Address,
        hasOfferValue: hasOfferValue(c),
        isSettled: isCaseSettled(c),
        OfferValue: c.OfferValue
      }))
    });



    // Process offers in database - keep LiveOffer as 0 (not sent) until email is actually sent
    // This prevents the frontend from showing "Offers sent" before the user confirms sending the email
    const processResult = await processOffers(casesWithOffers, {
      muniCode: negotiationObj.MuniCode,
      taxYear: negotiationObj.TaxYear,
      userId: user.userId,
      user: user,
      liveOffer: 0 // Keep offers as "not sent" until email is actually sent
    });

    if (!processResult.success) {
      logger.error('❌ [OFFER WORKFLOW] Process failed:', processResult);
      throw new Error(processResult.message || 'Failed to process offers');
    }

    
    // Return success result with email data for preview
    const successResult = {
      message: 'Offers processed successfully',
      processedCount: casesWithOffers.length,
      caseInfo,
      processResult,
      // Include email data for preview
      emailData: casesWithOffers.map(caseItem => ({
        parcel_id: caseItem.parcel_id,
        Address: caseItem.Address,
        OfferValue: convertOfferValueForEmail(caseItem),
        PortfolioOffer: caseItem.PortfolioOffer,
        // Add other necessary fields for email display
      })),
      emailOptions: {
        templateType: 'offers',
        downloadUrl: options.downloadUrl || '',
        data: casesWithOffers.map(caseItem => ({
          parcel_id: caseItem.parcel_id,
          Address: caseItem.Address,
          OfferValue: convertOfferValueForEmail(caseItem),
          PortfolioOffer: caseItem.PortfolioOffer,
          // Add other necessary fields for email display
        })),
        caseInfo: caseInfo
      }
    };
    onComplete(true, successResult);

  } catch (error) {
    logger.error('❌ [OFFER WORKFLOW] Error in offer workflow:', {
      error: error.message,
      stack: error.stack,
      selectedRowsCount: selectedRows?.length || 0,
      allRowsCount: allRows?.length || 0,
      hasNegotiationObj: !!negotiationObj,
      hasUser: !!user,
      userAccessLevel: user?.userAccess?.level
    });
    
    const errorResult = { error: error.message };
    logger.log('❌ [OFFER WORKFLOW] Calling onComplete with error:', errorResult);
    onComplete(false, errorResult);
  }
};


// THE NEW LOGIC FOR YOUR NEW FLOw - didnt bother rebuilding the whole thing bc this just worked.
/**
 * Handle combined offers and workups email workflow
 * Replicates the full offer flow and adds workup generation
 * @param {Object} options - Workflow options
 * @param {Array} options.selectedRows - Selected table rows
 * @param {Array} options.allRows - All table rows
 * @param {Object} options.negotiationObj - Negotiation object
 * @param {Object} options.user - User information
 * @param {string} options.workupType - Type of workup ('fnmaSummary' or 'onepagermaps')
 * @param {boolean} [options.RegenerateWorkups=true] - Whether to regenerate workups (batch attachment only)
 * @param {{ includeWorkups?: boolean, type?: 'batch'|'individual' }} [options.workupOptions] - Spreadsheet + email behavior; legacy `includeWorkups` fills defaults when omitted
 * @returns {Promise<Object>} Processing result with both URLs
 */
export const handleOffersAndWorkupsEmailWorkflow = async (options) => {
  const workupOptions = options.workupOptions ?? {
    includeWorkups: options.includeWorkups !== false,
    type: 'batch',
  };

  logger.log('🚀 [OFFERS+WORKUPS WORKFLOW] Starting combined flow:', {
    selectedRowsCount: options.selectedRows?.length || 0,
    allRowsCount: options.allRows?.length || 0,
    hasNegotiationObj: !!options.negotiationObj,
    hasUser: !!options.user,
    workupType: options.workupType || 'fnmaSummary',
    skipSubsetCheck: options.skipSubsetCheck || false,
    workupOptions,
    RegenerateWorkups: options.RegenerateWorkups !== false
  });

  const {
    selectedRows,
    allRows,
    negotiationObj,
    user,
    workupType = 'fnmaSummary',
    skipSubsetCheck = false,
    RegenerateWorkups = true
  } = options;

  const attachBatchWorkups =
    !!workupOptions.includeWorkups && workupOptions.type === 'batch';

  try {

    logger.log('running validation function...');
    // Run comprehensive validations (subset warning, missing offers, finalized, etc.)
    const validation = validateOfferEmailWorkflow({
      selectedRows,
      allRows,
      negotiationObj,
      user,
      skipSubsetCheck,
      skipWorkupsAboveIfmvCheck: options.skipWorkupsAboveIfmvCheck || false,
    });

    if (!validation.isValid) {
      logger.error('❌ [OFFERS+WORKUPS] Validation failed:', validation.error);
      
      return {
        success: false,
        error: validation.error,
        validationType: validation.validationType,
        missingOffers: validation.missingOffers,
        unfinalizedCases: validation.unfinalizedCases,
        missingCompsCases: validation.missingCompsCases,
        openCasesNotSelected: validation.openCasesNotSelected,
        unfinalizedCount: validation.unfinalizedCount,
        workupsAboveIfmv: validation.workupsAboveIfmv,
      };
    }

    logger.log('✅ [OFFERS+WORKUPS] All validations passed');
    
    const rowsToProcess = selectedRows;
    const unsettledSelectedCount = rowsToProcess.filter((row) => !isCaseSettled(row)).length;
    logger.log(`🔄 [OFFERS+WORKUPS] Processing ${rowsToProcess.length} selected cases (${unsettledSelectedCount} unsettled)`);

    const isIndividualWorkupSheet = workupOptions?.type === 'individual';
    if (isIndividualWorkupSheet && RegenerateWorkups) {
      const parcelIds = rowsToProcess.map((row) => row.parcel_id || row.id).filter(Boolean);
      const workupParcelIds = parcelIds.filter((parcelId) => {
        const caseData = findNegotiationCaseByParcelId(negotiationObj, parcelId);
        return !caseData || !isCaseSettled(caseData);
      });
      if (workupParcelIds.length === 0) {
        return {
          success: false,
          error: 'No unsettled cases selected for workups',
        };
      }
      const regenResult = await generateWorkupsForCombinedFlow(workupParcelIds, workupType, user?.userId);
      if (!regenResult.success) {
        return {
          success: false,
          error: regenResult.error || 'Failed to regenerate workups',
        };
      }
    }

    // Step 1: Generate offers XLS (matching regular offer flow exactly)
    logger.log('🔄 [OFFERS+WORKUPS] Generating offers spreadsheet...');

    const includeCompsInOffersSpreadsheet = useOfferOptionsStore.getState().includeCompsInOffersSpreadsheet;
    const negotiationObjFresh = usePersistedDataStore.getState().negotiationObj || negotiationObj;
    const caseDataForXls = mergeLatestS3FilesIntoCaseRows(allRows, negotiationObjFresh);
    const selectedRowsForXls = mergeLatestS3FilesIntoCaseRows(rowsToProcess, negotiationObjFresh);

    if (isIndividualWorkupSheet) {
      const workupLinks = await offerSheetWorkupLinks(selectedRowsForXls, workupType);
      selectedRowsForXls.forEach((row) => {
        const link = workupLinks[String(row.parcel_id || row.id)];
        if (link) row.WorkupLink = link;
      });
    }

    const s3UploadResult = await uploadOffersToS3({
      caseData: caseDataForXls,
      selectedRows: selectedRowsForXls,
      keys: getTownViewXLSKeys({
        includeCompsInOffersSheet: includeCompsInOffersSpreadsheet,
        workupOptions,
      }),
      townView: true,
      villageMode: negotiationObj?.villageRun === 1,
      avToggleActive: false,
      caseType: negotiationObj?.caseType || 'Town',
      workupReportTypeForXls: isIndividualWorkupSheet ? workupType : null,
    });

    if (!s3UploadResult.success) {
      logger.error('❌ [OFFERS+WORKUPS] Failed to generate offers spreadsheet:', s3UploadResult);
      throw new Error(`Failed to generate offers spreadsheet: ${s3UploadResult.error}`);
    }

    const offersDownloadUrl = s3UploadResult.downloadUrl;
    logger.log('✅ [OFFERS+WORKUPS] Offers spreadsheet generated:', offersDownloadUrl);

    // Step 2: Prepare email payload via EmailFactory (offers + optional workups)
    const parcelIds = rowsToProcess.map(row => row.parcel_id || row.id).filter(Boolean);
    const evidenceTypesByParcel = {};
    rowsToProcess.forEach((row) => {
      const id = row.parcel_id || row.id;
      const types = Array.isArray(row.SelectedEvidenceTypes)
        ? row.SelectedEvidenceTypes.filter((type) => type === 'Unequal' || type === 'Excessive')
        : [];
      if (id && types.length) evidenceTypesByParcel[String(id)] = types;
    });
    const combinedEmail = await prepareOffersAndOptionalWorkupsEmail({
      parcelIds,
      workupOptions,
      RegenerateWorkups: attachBatchWorkups && RegenerateWorkups,
      workupType,
      userId: user?.userId,
      evidenceTypesByParcel,
    });

    if (!combinedEmail.success) {
      logger.error('❌ [OFFERS+WORKUPS] Failed to prepare combined offers/workups email:', combinedEmail);
      throw new Error(combinedEmail.error || 'Failed to prepare combined offers/workups email');
    }

    const caseInfo = combinedEmail.emailOptions.caseInfo;

    // Step 3: Process offers (same as regular offer flow)
    logger.log('🔄 [OFFERS+WORKUPS] Processing offers in database...');
    
    const processResult = await processOffers(rowsToProcess, {
      muniCode: negotiationObj.MuniCode,
      taxYear: negotiationObj.TaxYear,
      userId: user.userId,
      user: user,
      liveOffer: 0 // Keep offers as "not sent" until email is actually sent
    });

    if (!processResult.success) {
      logger.error('❌ [OFFERS+WORKUPS] Failed to process offers:', processResult);
      throw new Error(processResult.message || 'Failed to process offers');
    }

    logger.log('combined email', combinedEmail);
    logger.log('offers download url', offersDownloadUrl);
    logger.log('workups download url', combinedEmail.emailOptions.workups_download_url);

    // Step 4: Return email data with both URLs
    return {
      success: true,
      message: attachBatchWorkups
        ? 'Offers and workups prepared successfully'
        : workupOptions?.type === 'individual'
          ? 'Offers prepared successfully (workup links in spreadsheet)'
          : 'Offers prepared successfully',
      workupOptions,
      processedCount: rowsToProcess.length,
      caseInfo,
      processResult,
      emailData: rowsToProcess.map(caseItem => ({
        parcel_id: caseItem.parcel_id,
        Address: caseItem.Address,
        OfferValue: convertOfferValueForEmail(caseItem),
        PortfolioOffer: caseItem.PortfolioOffer,
        SCARIndexNumber: caseItem.SCARIndexNumber || caseItem.scar_id
      })),
      emailOptions: {
        ...combinedEmail.emailOptions,
        offers_download_url: offersDownloadUrl,
      }
    };

  } catch (error) {
    logger.error('❌ [OFFERS+WORKUPS WORKFLOW] Error:', error);
    return {
      success: false,
      error: error.message || 'Failed to process offers and workups'
    };
  }
};
